import React from "react";
import axios from "axios";
import { MapPin, Clock, Loader2, Check, Search, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline pickup-point picker shown when the customer toggles
 * "Send til EMA Pickup Point" inside the booking flow.
 *
 * Props:
 *   - destinationZip: string  (the customer's intended ZIP to anchor the search)
 *   - selectedId: string | null
 *   - onSelect: (point) => void   // returns the full pickup-point object with address, coords, etc.
 */
export default function PickupPointSelector({ destinationZip = "", selectedId, onSelect }) {
  const [points, setPoints] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState(destinationZip || "");

  React.useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/pickup-points/public`)
      .then(({ data }) => setPoints(data.pickup_points || []))
      .catch(() => setPoints([]))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    if (destinationZip && !query) setQuery(destinationZip);
  }, [destinationZip, query]);

  // Filter + sort: prioritize matching ZIP, then alphabetical
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = points.slice();
    if (q) {
      list = list.filter(
        (p) =>
          (p.shop_name || "").toLowerCase().includes(q) ||
          (p.shop_zip || "").includes(q) ||
          (p.shop_city || "").toLowerCase().includes(q) ||
          (p.shop_address || "").toLowerCase().includes(q),
      );
    }
    // Bring zip-matches to the top
    if (destinationZip) {
      list.sort((a, b) => {
        const aMatch = a.shop_zip === destinationZip ? -1 : 0;
        const bMatch = b.shop_zip === destinationZip ? -1 : 0;
        return aMatch - bMatch;
      });
    }
    return list;
  }, [points, query, destinationZip]);

  if (loading) {
    return (
      <div className="border border-brand p-6 text-center text-brand-text2 flex items-center justify-center gap-2" data-testid="pp-selector-loading">
        <Loader2 className="animate-spin" size={14} /> Henter pickup points...
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <div className="border border-brand p-6 text-center text-brand-text2 text-sm" data-testid="pp-selector-empty">
        Der er endnu ingen aktive EMA Pickup Points. Vælg almindelig leveringsadresse i stedet.
      </div>
    );
  }

  return (
    <div className="border border-brand bg-white p-4 sm:p-5" data-testid="pp-selector">
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text2" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Søg postnummer, by eller butiksnavn..."
          className="input-flat pl-10"
          data-testid="pp-search"
        />
      </div>
      <div className="max-h-[400px] overflow-y-auto space-y-2" data-testid="pp-list">
        {filtered.length === 0 && (
          <div className="text-sm text-brand-text2 text-center py-6">
            Ingen pickup points matcher "{query}". Prøv et andet søgeord.
          </div>
        )}
        {filtered.map((p) => {
          const active = selectedId === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p)}
              data-testid={`pp-option-${p.id}`}
              className={`w-full text-left p-4 border-2 transition-all flex items-center gap-3 ${
                active ? "border-brand-red bg-red-50" : "border-brand hover:border-brand-dark"
              }`}
            >
              <div className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-full ${active ? "bg-brand-red text-white" : "bg-brand-bg2 text-brand-text2"}`}>
                {active ? <Check size={16} /> : <MapPin size={14} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-brand-dark truncate">{p.shop_name}</div>
                <div className="text-xs text-brand-text2 mt-0.5 truncate">{p.shop_address}, {p.shop_zip} {p.shop_city}</div>
                {p.opening_hours && (
                  <div className="text-xs text-brand-text2 mt-0.5 flex items-center gap-1">
                    <Clock size={10} /> {p.opening_hours}
                  </div>
                )}
              </div>
              <ChevronRight size={16} className={`shrink-0 ${active ? "text-brand-red" : "text-brand-text2"}`} />
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-brand-text2 mt-3 leading-relaxed">
        Modtageren får email + pickup-kode når pakken er klar til afhentning. Pakken kan hentes i butikkens åbningstid i op til 7 dage.
      </p>
    </div>
  );
}
